import { DatePicker, Input, Modal } from "@/components/basic";
import DeletePermission from "@/components/permissions/DeletePermission";
import { PermissionTable } from "@/components/permissions/PermissionsTable";
import { SharedPermissionTable } from "@/components/permissions/SharedPermissionsTable";
import { ToggleIsApprovablePermission } from "@/components/permissions/ToggleIsApprovablePermission";
import { AddAssignmentButton } from "@/components/resources/tabs/access-restrictions/AddAssignmentButton";
import { ExternalLink } from "@/components/ui/ExternalLink/ExternalLink";
import { SectionHeader } from "@/components/ui/SectionHeader/SectionHeader";
import usePermissions from "@/hooks/usePermissions";
import {
  ResourceSettingsActionType,
  useResourceSettingsState
} from "@/hooks/useResourceSettings";
import { useResourcesPermissions } from "@/hooks/useResourcesPermissions";
import useToggle from "@/hooks/useToggle";
import { api } from "@/lib/api/api";
import { areAll } from "@/lib/utils";
import { RESERVE_TEXTS } from "@/lib/utils-reserve";
import { IResource, IResourcePermission } from "@gettactic/api";
import {
  HookFormSelectInput,
  SelectItem
} from "@gettactic/components/src/form/SelectInput";
import { ConfirmModal } from "@gettactic/components/src/overlay/modals/ConfirmModal";
import { Box } from "@gettactic/components/src/primitives/Box";
import { Flex } from "@gettactic/components/src/primitives/Flex";
import { Icon } from "@gettactic/components/src/primitives/Icon";
import { useDisclosure, useStandardApiHandler } from "@gettactic/helpers";
import { getTimeDateObjectFromString } from "@gettactic/helpers/src/date-time/getTimeDateObjectFromString";
import { useAuthenticated } from "@gettactic/hooks";
import { useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { IoArrowDown, IoArrowForward } from "react-icons/io5";

interface AccessRestrictionsFormData {
  accessType: "open-access" | "restricted-access";
  hasAdditionalAccess: boolean;
  startsOn: string;
  startsAt: Date;
  allUsers: boolean;
}

type Props = {
  resources: IResource[];
  mutateAll: (err: boolean) => void;
};

export function groupSharedKey(perm: IResourcePermission) {
  return `${perm.user?.id}_${perm.team?.id}_${perm.since}_${perm.until}_${perm.is_approvable ? "true" : "false"}_${perm.access_group_type}`;
}

export type IResourcePermissionWithResName = IResourcePermission & {
  resName: string;
};

export type ConfirmOperationModal = {
  operation: "delete" | "is_approvable";
  applyTo: IResourcePermission[];
};

interface ChangeAccessDisclosureData {
  type: "open-access" | "restricted-access";
}

const getEmptyTimeDate = () => {
  const date = new Date();
  //date.setHours(0, 0, 0, 0);
  return date;
};

export default function RestrictionsTab({ resources, mutateAll }: Props) {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const [otherRestrictions, toggleOtherRestrictions] = useToggle();
  const [operationModal, setOperationModal] =
    useState<ConfirmOperationModal | null>(null);
  const { resourceIds, additionalAccess, refresh } = usePermissions(
    resources.map((resource) => resource.id)
  );

  const queryClient = useQueryClient();
  const permissions = useResourcesPermissions(resources);
  const accessChangeDisclosure = useDisclosure<ChangeAccessDisclosureData>();
  const resourceContext = useResourceSettingsState();

  const { wrapApiRequest, isLoading } = useStandardApiHandler();

  const allRestricted = areAll(
    resources.map((x) => x.is_open),
    false
  );

  const [accessMode, setAccessMode] = React.useState<
    "open-access" | "restricted-access"
  >(!allRestricted ? "open-access" : "restricted-access");

  const form = useForm<AccessRestrictionsFormData>({
    defaultValues: {
      hasAdditionalAccess: false,
      startsOn: "0",
      startsAt: getEmptyTimeDate(),
      allUsers: false
    }
  });

  const _hasAdditionalAccess = form.watch("hasAdditionalAccess");
  const hasAdditionalAccess =
    accessMode === "restricted-access" && _hasAdditionalAccess;

  const isAllUsersEnabled = form.watch("allUsers");

  const onAddAssignmentClose = () => {
    refresh();
    mutateAll(false);
  };

  const handleAccessChange = wrapApiRequest(
    async () => {
      const mode = accessChangeDisclosure.data?.type;
      if (!mode) return;

      setAccessMode(mode);

      if (!resources.length) {
        throw new Error("Resource not found");
      }

      const newResources = resources.map((resource) => ({
        ...resource,
        is_open: mode === "open-access"
      }));
      const result = newResources.map(async (newResource) => {
        await api.client.organizations.resourcePut(newResource.id, newResource);
      });
      await Promise.all(result);

      // should be only one resource at a time always
      newResources.forEach((newResource) => {
        resourceContext.dispatch({
          type: ResourceSettingsActionType.SetSelectedResource,
          payload: newResource
        });
      });
      await Promise.all([
        queryClient.invalidateQueries(["organizations.resourcesAll"]),
        queryClient.invalidateQueries([
          "organizations.resourceScheduledPermissionsAll"
        ]),
        queryClient.invalidateQueries([
          "organizations.getResourceAdditionalAccess"
        ])
      ]);
      await mutateAll(true);
      accessChangeDisclosure.close();
    },
    resources.length === 1
      ? "Resource access updated"
      : "Resources access updated"
  );

  const handleUpdateOptions = form.handleSubmit(
    wrapApiRequest(async (data) => {
      if (!resources.length) {
        throw new Error("Resource not found");
      }
      const result = resources.map(async (resource) => {
        await api.client.organizations.setResourceAdditionalAccessOptions(
          resource.id,
          {
            enabled: data.hasAdditionalAccess,
            starts_on: Number(data.startsOn),
            starts_at: `${data.startsAt.getHours()}:${data.startsAt.getMinutes()}`,
            all_users: data.allUsers
          }
        );
      });
      await Promise.all(result);
      await Promise.all([
        queryClient.invalidateQueries(["organizations.resourcesAll"]),
        queryClient.invalidateQueries([
          "organizations.resourceScheduledPermissionsAll"
        ]),
        queryClient.invalidateQueries([
          "organizations.getResourceAdditionalAccess"
        ])
      ]);
    }, "Resource access updated")
  );

  const sanitizedAdditionalAccess = JSON.stringify(additionalAccess);
  const sanitizedResource = JSON.stringify(resources);

  React.useEffect(() => {
    if (!additionalAccess || !additionalAccess[0].length) return;
    const resource = resources[0];
    if (!resource) return;
    if (sanitizedAdditionalAccess) {
      const nDate = getTimeDateObjectFromString(
        additionalAccess?.[0]?.[0]?.starts_at || ""
      );

      const allHasAllUsers = areAll(
        additionalAccess.map((x) => x[0]?.all_users),
        true
      );

      form.reset({
        hasAdditionalAccess: additionalAccess[0][0].enabled,
        startsOn: String(additionalAccess[0][0].starts_on || 0),
        startsAt: nDate,
        allUsers: allHasAllUsers
      });
    }
  }, [sanitizedAdditionalAccess, sanitizedResource]);

  const startAt = form.watch("startsAt");

  return (
    <>
      <Flex className={"mt-6 flex-col gap-6"}>
        <Flex className={"w-full gap-6"}>
          <div className={"w-full"}>
            <Flex as={"label"} className={"gap-4"}>
              <Box>
                <Input
                  type="radio"
                  value={"open-access"}
                  checked={accessMode === "open-access"}
                  onChange={(event) => {
                    accessChangeDisclosure.open({ type: "open-access" });
                  }}
                />
              </Box>
              <Flex className={"flex-col gap-0.5"}>
                <Icon name={"Users"} className={"text-disabled"} />
                <Box className={"font-bold text-lg"}>Open Access</Box>
                <Box className={"text-muted-foreground text-sm"}>
                  {resources.length === 1
                    ? "Anybody can reserve this resource."
                    : "Anybody can reserve these resources."}
                </Box>
              </Flex>
            </Flex>
          </div>
          <div className={"w-full"}>
            <Flex as={"label"} className={"gap-4"}>
              <Box>
                <Input
                  type="radio"
                  value={"restricted-access"}
                  checked={accessMode === "restricted-access"}
                  onChange={(event) => {
                    accessChangeDisclosure.open({ type: "restricted-access" });
                  }}
                />
              </Box>

              <Flex className={"flex-col gap-0.5"}>
                <Icon name={"Lock"} className={"text-disabled"} />
                <Box className={"font-bold text-lg"}>Restricted Access</Box>
                <Box className={"text-muted-foreground text-sm"}>
                  {resources.length === 1
                    ? "Only those assigned to this resource can reserve it."
                    : "Only those assigned to these resources can reserve it."}
                </Box>
              </Flex>
            </Flex>
          </div>
        </Flex>

        <div>
          <Flex as={"label"} className={"select-none gap-4"}>
            <Box>
              <Input
                type="checkbox"
                className="rounded checked:bg-primary checked:hover:bg-primary-hover focus:ring-0 focus:ring-primary checked:focus:bg-primary"
                {...form.register("hasAdditionalAccess", {
                  onChange: handleUpdateOptions
                })}
                disabled={accessMode === "open-access"}
                readOnly={accessMode === "open-access"}
              />
            </Box>
            <Box className={"pt-0.5"}>
              <Box className={"font-bold text-lg"}>Priority Booking</Box>
              <Box className={"text-muted-foreground text-sm"}>
                If no reservations have been made by the primary group, then you
                can optionally add access to a secondary group. Select when the
                secondary group gains access.
              </Box>
            </Box>
          </Flex>
          {hasAdditionalAccess ? (
            <Flex className={"flex-col px-8 pt-2 pb-6"}>
              <Flex className={"items-center gap-6"}>
                <Box>Days</Box>
                <HookFormSelectInput
                  className={"max-w-[200px]"}
                  name={"startsOn"}
                  form={form}
                  onChange={handleUpdateOptions}
                >
                  <SelectItem value={"0"}>Same Day</SelectItem>
                  <SelectItem value={"-1"}>1 Day Before</SelectItem>
                  <SelectItem value={"-2"}>2 Days Before</SelectItem>
                  <SelectItem value={"-3"}>3 Days Before</SelectItem>
                  <SelectItem value={"-4"}>4 Days Before</SelectItem>
                  <SelectItem value={"-5"}>5 Days Before</SelectItem>
                  <SelectItem value={"-6"}>6 Days Before</SelectItem>
                  <SelectItem value={"-7"}>1 Week Before</SelectItem>
                </HookFormSelectInput>
                <Box>Time</Box>
                <DatePicker
                  selected={startAt || getEmptyTimeDate()}
                  onChange={(date) => {
                    form.setValue("startsAt", date || new Date());
                    handleUpdateOptions();
                  }}
                  className="input w-full rounded-md"
                  calendarClassName="tailwind-timepicker"
                  wrapperClassName="tailwind-timepicker-wrapper"
                  placeholderText={RESERVE_TEXTS.selectTime}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption={RESERVE_TEXTS.selectTimeTitle}
                  timeFormat="h:mmaaa"
                  dateFormat="h:mmaaa"
                />
              </Flex>
              <Flex as={"label"} className={"select-none gap-4 pt-4"}>
                <Box>
                  <Input
                    type="checkbox"
                    className="rounded checked:bg-primary checked:hover:bg-primary-hover focus:ring-0 focus:ring-primary checked:focus:bg-primary"
                    {...form.register("allUsers", {
                      onChange: handleUpdateOptions
                    })}
                  />
                </Box>
                <Box className={"pt-0.5"}>
                  <Box className={""}>
                    Add
                    <span className="mx-1 inline-flex font-bold capitalize">
                      All {authenticatedUser?.organization?.name} Users
                    </span>
                    to the Secondary Assignment Group
                  </Box>
                </Box>
              </Flex>
            </Flex>
          ) : null}
        </div>
      </Flex>
      <ConfirmModal
        isOpen={accessChangeDisclosure.isOpen}
        onOpenChange={accessChangeDisclosure.setOpen}
        onConfirm={handleAccessChange}
        title={"Change Resource's Access Type"}
        message={
          <>
            Are you sure you want to change the access type of the selected
            resources to{" "}
            <strong>{accessChangeDisclosure.data?.type || ""}</strong> ?
          </>
        }
      />
      <>
        <SectionHeader
          title={"Assignments"}
          actions={
            <AddAssignmentButton
              resources={resources}
              hasAdditionalAccess={hasAdditionalAccess}
              isAllUsersEnabled={isAllUsersEnabled}
              onRefresh={onAddAssignmentClose}
            />
          }
          info={
            <>
              Assignments can be used either to restrict a resource or to
              require approval for specific teams &amp; users. See{" "}
              <ExternalLink href="https://gettactic.zendesk.com/hc/en-us/articles/27811510535067-Add-Edit-or-Delete-Workspaces">
                the help article
              </ExternalLink>{" "}
              for additional details.
            </>
          }
        />

        <Modal
          title={
            operationModal?.operation === "delete"
              ? "Delete"
              : "Requires Approval"
          }
          opened={operationModal ? operationModal.applyTo.length > 0 : false}
          onClose={() => setOperationModal(null)}
          size="lg"
        >
          {(operationModal ? operationModal.applyTo.length > 0 : false) ? (
            operationModal?.operation === "delete" ? (
              <DeletePermission
                resourceIds={resourceIds}
                permissions={operationModal?.applyTo ?? []}
                onClose={() => {
                  setOperationModal(null);
                  mutateAll(false);
                }}
              />
            ) : (
              <ToggleIsApprovablePermission
                resourceIds={resourceIds}
                permissions={operationModal?.applyTo ?? []}
                onClose={() => {
                  setOperationModal(null);
                  mutateAll(false);
                }}
              />
            )
          ) : null}
        </Modal>

        {resources.length > 1 && permissions.shared.length ? (
          <h3 className="mt-6 mb-4 font-bold text-lg text-secondary">
            Shared Assignments with the Selected Resources ({resources.length})
          </h3>
        ) : null}

        {permissions.shared?.length ? (
          <SharedPermissionTable
            setOperationalModal={setOperationModal}
            data={permissions.shared}
            inGroups={permissions.inGroups}
          />
        ) : null}

        {resources.length > 1 && permissions.rest.length ? (
          <>
            <h3
              onClick={toggleOtherRestrictions}
              className="mt-6 mb-4 cursor-pointer font-bold text-lg text-secondary"
            >
              {otherRestrictions ? (
                <IoArrowDown className="inline h-4 w-4" />
              ) : (
                <IoArrowForward className="inline h-4 w-4" />
              )}{" "}
              Non-Shared Assignments ({permissions.rest.length})
            </h3>
            {otherRestrictions ? (
              <PermissionTable
                setOperationalModal={setOperationModal}
                data={permissions.rest}
              />
            ) : null}
          </>
        ) : null}
      </>
    </>
  );
}
